import { z } from "zod";

import { createProfileDataModel } from "@/domains/profile/factories/create-profile-data-model";
import { LOGIN_ERRORS, PENDING_ACTIONS, PROFILING_ERRORS } from "./constants";

export const setPasswordResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.literal("bearer"),
});

export const profileResponseSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  phone: z.string(),
  birthDate: z.string(),
  type: z.string(),
  isTestUser: z.boolean(),
  isPersonify: z.boolean(),
  gender: z.string(),
  smsEnabledAt: z.string().nullable(),
  emailsEnabledAt: z.string().nullable(),
  klaviyoProfileId: z.string().nullable(),
  profilingId: z.number(),
  careplanRequiresReview: z.boolean(),
  lastSession: z.string().nullable(),
  createdAt: z.string(),
  sms_communication_permissions: z.boolean().nullish(),
  communication_permissions: z.boolean().nullish(),
});

export const profileDataModelSchema = profileResponseSchema.transform(createProfileDataModel);

export const pendingActionsSchema = z.enum(PENDING_ACTIONS);

export const pendingActionResponseSchema = z.object({
  action: pendingActionsSchema,
});

export const profilingUrlSchema = z.object({
  responder_uuid: z.string().optional(),
  error: z.enum(PROFILING_ERRORS).optional(),
  fbp: z.string().optional(),
  fbc: z.string().optional(),
  fbclid: z.string().optional(),
});

export const loginUrlSchema = z.object({
  redirect: z.string().optional(),
  error: z.enum(LOGIN_ERRORS).optional().catch(undefined),
});

export const onboardingExchangeResponseSchema = z.object({
  data: z.object({
    linkToken: z.string(),
    provider: z.string(),
    expiresAt: z.number(),
  }),
});

export const onboardingStatusResponseSchema = z.object({
  data: z.object({
    ready: z.boolean(),
  }),
});

export const emailCheckFormSchema = z.object({
  email: z.email("Email not valid"),
});

export const verifyEmailResponseSchema = z.object({
  HP: z.boolean(),
});

export const emailCheckAnswerTypeSchema = z.object({
  address: z.email(),
  HP: z.boolean(),
});

export const feedbackAnswersSchema = z
  .object({
    dosage: z.string().nonempty(),
    dosageId: z.string().optional(),
    relief: z.string().nonempty(),
    hasSideEffects: z.boolean(),
    sideEffects: z.array(z.string()),
    // Intensity (1-5) keyed by side-effect label, only for the model-relevant ones.
    sideEffectIntensities: z.record(z.string(), z.number()).optional(),
  })
  .refine(
    (data) => {
      return !data.hasSideEffects || data.sideEffects.length > 0;
    },
    {
      message: "Select at least one side effect",
      path: ["sideEffects"],
    },
  );

export const hpFeedbackFormSchema = z.object({
  selectedProducts: z.array(z.string()).max(3, "You can only select up to 3 products"),
  skipped: z.boolean(),
  feedback: z.record(z.string(), feedbackAnswersSchema),
});

export const hpFeedbackAnswerTypeSchema = z.object({
  skipped: z.boolean(),
  hp_selected_products: z.array(z.string()),
  hp_product_feedbacks: z.array(
    z.object({
      sku: z.string(),
      how_much_typically_take_it: z.string(),
      how_much_typically_take_it_id: z.string().optional(),
      how_much_relief: z.string(),
      has_unwanted_side_effects: z.boolean(),
      unwanted_side_effects: z.array(z.string()).optional(),
      side_effect_intensities: z
        .array(z.object({ side_effect: z.string(), intensity: z.number() }))
        .optional(),
    }),
  ),
});

export const otpVerificationSchema = z.object({
  code: z
    .string()
    .nonempty("Code is required")
    .regex(/^\d{6}$/, "Code must be 6 digits"),
});

export const otpVerifiedUserSchema = z.object({
  hasCbdOnly: z.boolean(),
  hasThcProduct: z.boolean(),
  products: z.array(
    z.object({
      sku: z.string(),
      name: z.string(),
      url: z.string(),
      productType: z.string(),
      thcCbdType: z.string(),
      doses: z.array(z.object({ id: z.string(), key: z.string(), value: z.string() })),
      sideEffects: z.array(z.string()),
    }),
  ),
  verified: z.literal(true),
});
